import React from "react";
import { NavLink, Link } from "react-router-dom";
import {
  Home,
  Activity,
  ClipboardList,
  HardHat,
  FileWarning,
  ShieldAlert,
  Siren,
  BarChart3,
  Building2,
  Tablet,
  Settings,
  Crown,
  Users,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * v1.2 — Refactored sidebar: operational items only.
 * - 11 top items, role-gated (host=1, receptionist=2, site_manager=3,
 *   org_admin=4, super_admin=5).
 * - Sticky Settings gear pinned to the bottom — duplicates the header gear
 *   per the IA (both go to /settings).
 * - Settings sub-pages do NOT appear here — they're inside SettingsLayout.
 */

const ROLE_LEVEL = { host: 1, receptionist: 2, site_manager: 3, org_admin: 4, super_admin: 5 };

const ITEMS = [
  { to: "/dashboard",            label: "Home",              icon: Home,         minRole: "host",         testid: "nav-home" },
  { to: "/dashboard/live",       label: "Live",              icon: Activity,     minRole: "receptionist", testid: "nav-live" },
  { to: "/pre-registrations",    label: "Pre-registrations", icon: ClipboardList,minRole: "host",         testid: "nav-pre-regs", tour: "nav-preregs" },
  { to: "/contractors",          label: "Contractors",       icon: HardHat,      minRole: "site_manager", testid: "nav-contractors" },
  { to: "/permits-to-work",      label: "Permits",           icon: FileWarning,  minRole: "site_manager", testid: "nav-permits" },
  { to: "/admin/watchlist-hits", label: "Watchlist",         icon: ShieldAlert,  minRole: "receptionist", testid: "nav-watchlist" },
  { to: "/evacuation",           label: "Evacuation",        icon: Siren,        minRole: "site_manager", testid: "nav-evacuation" },
  { to: "/reports",              label: "Reports",           icon: BarChart3,    minRole: "receptionist", testid: "nav-reports" },
  { to: "/sites",                label: "Sites",             icon: Building2,    minRole: "host",         testid: "nav-sites",     tour: "nav-sites" },
  { to: "/kiosks",               label: "Kiosks",            icon: Tablet,       minRole: "site_manager", testid: "nav-kiosks",    tour: "nav-kiosks" },
  { to: "/users",                label: "Users",             icon: Users,        minRole: "org_admin",    testid: "nav-users",     tour: "nav-users" },
];

export default function Sidebar() {
  const { user } = useAuth();
  const role = user?.role || "host";
  const myLevel = ROLE_LEVEL[role] || 0;
  const visible = ITEMS.filter((it) => myLevel >= ROLE_LEVEL[it.minRole]);

  return (
    <aside
      className="hidden md:flex flex-col w-60 bg-white dark:bg-navy-800 border-r border-navy-100 dark:border-navy-700 flex-shrink-0"
      data-testid="sidebar"
    >
      {/* Logo / brand */}
      <Link to="/dashboard" className="px-5 py-4 border-b border-navy-100 dark:border-navy-700 flex items-center gap-2" data-testid="sidebar-brand">
        <span className="w-7 h-7 rounded-md bg-navy-900 text-white flex items-center justify-center text-xs font-bold">EB</span>
        <span className="font-semibold text-navy-900 dark:text-white">EntryBook</span>
      </Link>

      {/* Primary nav */}
      <nav className="flex-1 overflow-y-auto py-3 px-2">
        {visible.map((it) => {
          const Icon = it.icon;
          return (
            <NavLink
              key={it.to}
              to={it.to}
              end={it.to === "/dashboard"}
              data-testid={it.testid}
              data-tour={it.tour}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors mb-0.5 ${
                  isActive
                    ? "bg-orange-500/10 text-orange-700 dark:text-orange-300"
                    : "text-navy-700 dark:text-navy-200 hover:bg-navy-50 dark:hover:bg-navy-700 hover:text-navy-900 dark:hover:text-white"
                }`
              }
            >
              <Icon className="w-4 h-4" />
              {it.label}
            </NavLink>
          );
        })}
      </nav>

      {/* Sticky bottom — Settings + Super admin entry (if super_admin) */}
      <div className="border-t border-navy-100 dark:border-navy-700 p-2">
        {role === "super_admin" && (
          <NavLink
            to="/super-admin"
            data-testid="nav-super-admin"
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors mb-1 ${
                isActive
                  ? "bg-[#FF8A4C]/15 text-[#FF8A4C]"
                  : "text-navy-700 dark:text-navy-200 hover:bg-navy-50 dark:hover:bg-navy-700 hover:text-navy-900 dark:hover:text-white"
              }`
            }
          >
            <Crown className="w-4 h-4" />
            Super admin
          </NavLink>
        )}
        <NavLink
          to="/settings"
          data-testid="nav-settings"
          className={({ isActive }) =>
            `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
              isActive
                ? "bg-navy-100 dark:bg-navy-700 text-navy-900 dark:text-white"
                : "text-navy-600 dark:text-navy-300 hover:bg-navy-50 dark:hover:bg-navy-700 hover:text-navy-900 dark:hover:text-white"
            }`
          }
        >
          <Settings className="w-4 h-4" />
          Settings
        </NavLink>
      </div>
    </aside>
  );
}
